import { digger } from '@cbre/digger';
import { concatValuesBy, defaultTxt, EMPTY_STRING, simpleValueValidation } from '@miq/utils';
import { Observable } from 'rxjs';
import { DataObject, ReportRecord } from '../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  DisplayFunctionPPT,
  LovValues,
} from '../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../utils/dynamic-data-retrievers';
import { getCompanyFieldsInfo } from '../fields-args.const';

export const DOLLAR_SIGN = '$';
export const PERCENT_SIGN = '%';

export enum PropertyType {
  Industrial = 'Industrial',
  Office = 'Office',
  Retail = 'Retail',
}

export const extraCurrencyValues: LovValues[] = [
  { key: 'Swedish Krona', value: 'SEK' },
  { key: 'Norwegian Krone', value: 'NOK' },
];

function mapUsagesField(propertyType: PropertyType, arg: DisplayFnArgumentsInfo): DisplayFnArgumentsInfo {
  if (arg?.field != null && arg.field.includes('usages')) {
    const field = arg.field.split('usages/').join(`usages[ref_property_usage_type_desc='${propertyType}'].`);
    arg = { ...arg, field };
  }
  if (arg?.path != null && arg.path.includes('usages')) {
    const path = arg.path.split('usages.').join(`usages[ref_property_usage_type_desc='${propertyType}'].`);
    arg = { ...arg, path };
  }
  return arg;
}

export function formatCompanyRoleVals(record: ReportRecord, arg: DisplayFnArgumentsInfo, labelId: number): string {
  arg = {
    ...arg,
    ...getCompanyFieldsInfo(labelId),
  };
  const companyVals = mapAndFormatDynamicValue(record, arg);
  return Array.isArray(companyVals) ? (companyVals || []).join(', ') : companyVals;
}

export function propertyTypeDisplayFnFactory(
  propertyType: PropertyType,
  customDisplayFn?: DisplayFunctionPPT
): DisplayFunctionPPT {
  return (record: ReportRecord, options, arg: DisplayFnArgumentsInfo) => {
    const displayFn = customDisplayFn != null ? customDisplayFn : displayFnWrapper;
    const uomArgument = arg?.uomArgument != null ? mapUsagesField(propertyType, arg.uomArgument) : null;
    arg = mapUsagesField(propertyType, arg);
    return displayFn(record, options, { ...arg, uomArgument });
  };
}

export function currencyLov(options: DisplayFunctionOptions): Observable<LovValues[]> {
  const { locale, reportExportViewService } = options;
  const lov$ = reportExportViewService.getLovObservablesData(
    '/lookup/currency/symbols',
    undefined,
    locale
  ) as Observable<LovValues[]>;
  return lov$;
}

export function valueWithUomPrefix(
  record: ReportRecord,
  propertyType: PropertyType,
  argument: DisplayFnArgumentsInfo,
  currencyLOV: LovValues[]
): string | string[] {
  function customFn(rec: ReportRecord, _, arg: DisplayFnArgumentsInfo) {
    return mapAndFormatDynamicValue(rec, arg, currencyLOV);
  }
  const localDisplayFn = propertyTypeDisplayFnFactory(propertyType, customFn);
  const result = localDisplayFn(record, undefined, {
    ...argument,
    uomArgument: { ...argument.uomArgument, isPrefix: true },
  }) as string | string[];
  return result;
}

export function getClearHeight(
  record: ReportRecord,
  options: DisplayFunctionOptions,
  min: DisplayFnArgumentsInfo,
  max: DisplayFnArgumentsInfo
) {
  const industrialDisplayFnWrapper = propertyTypeDisplayFnFactory(PropertyType.Industrial);
  const getFirst = (val: string | string[]) => (Array.isArray(val) ? val[0] : val);
  let minVal = industrialDisplayFnWrapper(record, options, min) as string | string[];
  let maxVal = industrialDisplayFnWrapper(record, options, max) as string | string[];
  minVal = getFirst(minVal);
  maxVal = getFirst(maxVal);
  return minVal === defaultTxt && maxVal === defaultTxt ? defaultTxt : concatValuesBy(' - ', minVal, maxVal);
}

export const RoleDescriptions = {
  Tenant: 'Tenant',
  Lessee: 'Lessee',
  TenantRep: 'Tenant Representative',
  LesseeRep: 'Lessee Representative',
  ListingRep: 'Listing Representative',
  ReportingBroker: 'Reporting Broker',
  TrueBuyer: 'True Buyer',
  LegalBuyer: 'Legal Buyer',
  BuyerRep: 'Buyer Representative',
  LegalSeller: 'Legal Seller',
  TrueSeller: 'True Seller',
  SellerRep: 'Seller Representative',
};

/**
 * If MAX UOM is null for all records, show no UOM in header. If it is the combination of SFs and null, return SF in header
 * If MAX UOMs for different records are not unique,  do not show UOM in header.
 */
export function getUOMValue(uom: string, uomValue: string): string {
  if (uom !== '' && uomValue) {
    if (uom === null) {
      return uomValue;
    }
    if (uom !== uomValue) {
      return '';
    }
  }
  return uom;
}

/**
 * Format Company/Contact records into string using company name and contact
 * display name.
 *
 * @param record
 * @param attributeInfo
 * @param requiredRole
 *
 * @example
 * Expected output from this method for a single tim record
 * CompanyName1 - Contact1,Contact2,Contact3
 * CompanyName2 - Contact2,Contact3,Contact4
 */
export function formatCompanyContactByRole(
  record: ReportRecord,
  attributeInfo: DisplayFnArgumentsInfo,
  requiredRole: string | string[],
  separator: string,
  altField?: string
): string {
  if (!simpleValueValidation(attributeInfo.field ?? altField)) {
    return defaultTxt;
  }
  const rolesToFilter = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const companyContactRecords = digger(record.data, altField ?? 'usages/company_contact_role_addresses');
  const uniqueCompanyIds = [...new Set(companyContactRecords.map(item => item.company_id))];
  const formattedCompanyContacts: string[] = uniqueCompanyIds.map(companyId => {
    // filter company/contact record by id and role
    const filteredCompanyContacts = companyContactRecords.filter(
      itemContact => itemContact.company_id === companyId && rolesToFilter.includes(itemContact.role_desc)
    );
    if (filteredCompanyContacts.length === 0) {
      return EMPTY_STRING;
    }
    let companyName: string;
    companyName = filteredCompanyContacts[0].party_organization_display_name;
    // join contact display names
    const contactsDisplayName = filteredCompanyContacts
      .filter(item => !!item.contact_id)
      .map(itemContact => itemContact.familiar_name ?? '-')
      .join(', ');
    if (companyName == null) {
      return EMPTY_STRING;
    }
    if (contactsDisplayName) {
      return `${companyName} ${separator} ${contactsDisplayName}`;
    }
    return companyName;
  });
  return formattedCompanyContacts.length > 0
    ? formattedCompanyContacts.filter(data => data != EMPTY_STRING).join('\n')
    : defaultTxt;
}

/**
 * Function that makes groups a set of elements by the value of given attribute
 * @param attr
 * @returns
 */
const groupBy = (attr: string) => (elements: DataObject[]) =>
  elements
    .filter(x => !!x[attr])
    .reduce((acc, next) => ({ ...acc, [next[attr]]: acc[next[attr]] ? [...acc[next[attr]], next] : [next] }), {});

/**
 * GetContactInfo options
 */
export interface GetContactInfoByRolesOptions {
  /**
   * text to use as separator between contact info of the same role
   * e.g if contactSeparator = `::` then a list of contacts would look like
   * `contact one::concat two::three`
   */
  contactSeparator?: string;
  /**
   * text to be used when familiar_name is empty or null
   */
  defaultText?: string;
}

export interface GetCompanyAndContactInfoByRolesOptions extends GetContactInfoByRolesOptions {
  /**
   * text to use as separator between contact info of the same role
   * e.g if contactSeparator = `::` then a list of contacts would look like
   * `company one::company two::three`
   */
  companySeparator?: string;
  /**
   * text to be used as union indicator between companies and contacts
   * e.g if companyContactUnion = `:=` and each company contains at least 2 contacts then
   * `company one:=contact one, contact two; company two:=concat three`
   * (`, ` separation is given by `contactSeparator` value and `; `is set by `companySeparator`)
   */
  companyContactUnion?: string;
}

/**
 * Returns a function that retrieves company & contact information git a configured format
 * default format would render companies and its contacts like
 * `Company 1 | contact one, contact two; Company 2 | contact three; Company 3 | -`
 * @param targetRoles
 * @returns
 */
export const getCompanyAndContactInfoByRoles =
  (...targetRoles: string[]) =>
  (record: DataObject, opts?: GetCompanyAndContactInfoByRolesOptions): string => {
    const { companySeparator = '; ', companyContactUnion = ' | ' } = opts ?? {};
    const companiesInTargetRolesQuery = makePathForCompaniesWithMultipleRoles(...targetRoles);
    // get the list of contacts with any of the given roles
    let companies = digger(record, companiesInTargetRolesQuery);
    // if none return '-'
    if (companies == null) return defaultTxt;

    // ensure found item is an array
    if (!Array.isArray(companies)) companies = [companies];

    const groupByCompanyName = groupBy('party_organization_display_name');
    const contactsByCompanyName = groupByCompanyName(companies);
    // transform found information into a string
    return Object.entries(contactsByCompanyName)
      .map(([companyName, contacts]) => `${companyName}${companyContactUnion}${getContactFamiliarName(contacts, opts)}`)
      .join(companySeparator);
  };

/**
 * Returns the familiar name of given list of contacts formatting with given separation options
 * @param companyContacts
 * @param opts
 * @returns
 */
export const getContactFamiliarName = (
  companyContacts: { contact_id: any; familiar_name: string }[],
  opts?: GetContactInfoByRolesOptions
) => {
  const { defaultText = defaultTxt, contactSeparator = ', ' } = opts ?? {};
  return (
    companyContacts
      .filter(item => item.contact_id)
      .map(({ familiar_name }) => familiar_name ?? defaultText)
      .join(contactSeparator) || defaultText
  );
};

/**
 * Returns the basic value for a jsonata condition that includes the given set of roles as
 * or condition
 * @param roles
 * @returns
 */
export function makePathForCompaniesWithMultipleRoles(...roles: string[]) {
  const rolesOrCondition = roles.map(role => `role_desc='${role}'`).join(' or ');
  return `company_contact_role_addresses[${rolesOrCondition}]`;
}
